import { useCallback, useEffect, useRef, useState } from "react";
import { CHAT_HISTORY_UPDATED, newChatHistory, readChatHistory, writeChatHistory } from "@/lib/chatHistory";

export default function useChatHistory(storageKey) {
  const [history, setHistory] = useState(() => readChatHistory(storageKey) || newChatHistory());
  const current = useRef(history);
  const mounted = useRef(false);

  const latest = useCallback(() => {
    const saved = readChatHistory(storageKey);
    if (saved && saved.revision > current.current.revision) current.current = saved;
    return current.current;
  }, [storageKey]);

  useEffect(() => {
    mounted.current = true;
    const sync = (event) => {
      if (event && event.key !== storageKey && event.detail?.key !== storageKey) return;
      const saved = readChatHistory(storageKey);
      if (saved && saved.revision > current.current.revision) {
        current.current = saved;
        setHistory(saved);
      }
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(CHAT_HISTORY_UPDATED, sync);
    return () => {
      mounted.current = false;
      window.removeEventListener("storage", sync);
      window.removeEventListener(CHAT_HISTORY_UPDATED, sync);
    };
  }, [storageKey]);

  const update = useCallback((transform) => {
    const previous = latest();
    const messages = transform(previous.messages);
    if (!messages) return;
    const next = { ...previous, revision: previous.revision + 1, messages };
    current.current = next;
    if (mounted.current) setHistory(next);
    // A reply received after unmount still belongs to the captured user/business.
    writeChatHistory(storageKey, next);
  }, [latest, storageKey]);

  const appendMessage = useCallback((message) => update((messages) => [...messages, message]), [update]);
  const ensureGreeting = useCallback((greeting) => {
    if (typeof greeting === "string") update((messages) => messages.length ? null : [{ role: "assistant", content: greeting }]);
  }, [update]);
  const getSessionId = useCallback(() => latest().sessionId, [latest]);

  return { messages: history.messages, appendMessage, ensureGreeting, getSessionId, mounted };
}
